<template>
  <div id="mind-wrapper">
    <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 16px">
      <div style="flex: 1; max-width: 400px;">
        <a-input-search
          v-model="searchText"
          placeholder="搜索片区名称"
          allow-clear
          @search="handleSearch"
          @change="onSearchChange"
        >
          <a-button slot="enterButton" type="primary" :disabled="!searchText">
            搜索
          </a-button>
        </a-input-search>
        <div v-if="searchResults.length > 0" style="margin-top: 8px; color: #666; font-size: 12px;">
          找到 {{ searchResults.length }} 个结果
          <a-button-group size="small" style="margin-left: 8px;">
            <a-button 
              :disabled="searchResults.length === 0 || currentResultIndex === 0"
              @click="navigateSearchResult(-1)"
            >
              上一个
            </a-button>
            <a-button 
              :disabled="searchResults.length === 0 || currentResultIndex === searchResults.length - 1"
              @click="navigateSearchResult(1)"
            >
              下一个
            </a-button>
          </a-button-group>
          <span style="margin-left: 8px;">{{ currentResultIndex + 1 }}/{{ searchResults.length }}</span>
        </div>
      </div>
      <div>
        <a-button style="margin-right: 10px;" @click="handleFullscreen">
          <a-icon :type="isFullscreen ? 'fullscreen-exit' : 'fullscreen'" />
          {{ isFullscreen ? '退出全屏' : '全屏' }}
        </a-button>
        <a-button style="margin-right: 10px;" @click="handleReset">重置</a-button>
        <a-button type="primary" @click="handleSave">保存</a-button>
      </div>
    </div>
    <div id="mind-container"></div>
  </div>
</template>

<script>
import G6 from '@antv/g6'

let graph = null

export default {
  props: {
    gData: {
      type: Array,
      required: true,
      default: () => [],
    },
  },
  data () {
    return {
      initialTreeData: null,
      isFullscreen: false,
      searchText: '',
      searchResults: [],
      currentResultIndex: 0,
      highlightedNode: null,
    }
  },
  mounted () {
    this.initMindMap()
  },
  methods: {
    // 递归设置节点的收缩状态：2级及以下的节点默认收缩
    setCollapseStateByLevel (data, level = 1) {
      if (!data || !Array.isArray(data)) {
        return
      }

      data.forEach(item => {
        // 第2级及以下（level >= 2）的节点设置为收缩状态
        if (level >= 2) {
          item.collapsed = true
        } else {
          // 第1级默认展开
          item.collapsed = false
        }

        // 递归处理子节点
        if (item.children && Array.isArray(item.children) && item.children.length > 0) {
          this.setCollapseStateByLevel(item.children, level + 1)
        }
      })
    },
    initMindMap () {
      const container = document.getElementById('mind-container')
      const width = container.scrollWidth
      const height = container.scrollHeight || 1000

      // 文本换行辅助函数
      const wrapText = (text, maxWidth, fontSize = 12, maxLines = 4) => {
        if (!text) return []
        const words = text.split('')
        const lines = []
        let currentLine = ''

        // 估算字符宽度（中文字符约为 fontSize，英文字符约为 fontSize * 0.6）
        const getCharWidth = (char) => {
          return /[\u4e00-\u9fa5]/.test(char) ? fontSize : fontSize * 0.6
        }

        for (let i = 0; i < words.length; i++) {
          const char = words[i]
          const testLine = currentLine + char
          const testWidth = testLine.split('').reduce((sum, c) => sum + getCharWidth(c), 0)

          // 如果当前行加上新字符会超出宽度，且当前行不为空
          if (testWidth > maxWidth && currentLine !== '') {
            // 如果已达到最大行数，截断并添加省略号
            if (lines.length >= maxLines - 1) {
              // 尝试在最后一行添加省略号
              let truncatedLine = currentLine
              while (truncatedLine.length > 0) {
                const testTruncated = truncatedLine + '...'
                const testTruncatedWidth = testTruncated.split('').reduce((sum, c) => sum + getCharWidth(c), 0)
                if (testTruncatedWidth <= maxWidth) {
                  lines.push(testTruncated)
                  break
                }
                truncatedLine = truncatedLine.slice(0, -1)
              }
              // 如果无法添加省略号，至少添加当前行的一部分
              if (truncatedLine.length === 0 && currentLine.length > 0) {
                lines.push(currentLine.slice(0, Math.max(1, Math.floor(maxWidth / fontSize * 0.8))))
              }
              break
            } else {
              // 未达到最大行数，正常换行
              lines.push(currentLine)
              currentLine = char
            }
          } else {
            currentLine = testLine
          }
        }

        // 添加最后一行（如果未达到最大行数）
        if (currentLine && lines.length < maxLines) {
          lines.push(currentLine)
        } else if (currentLine && lines.length === 0) {
          // 如果只有一行但超出宽度，至少显示一部分
          lines.push(currentLine)
        }

        return lines.length > 0 ? lines : [text]
      }

      // 注册自定义节点类型，带收缩按钮
      G6.registerNode('tree-node-with-collapse', {
        draw (cfg, group) {
          const size = cfg.size || [80, 40]
          let width = size[0]
          let height = size[1]
          const style = cfg.style || {}
          const fontSize = style.fontSize || 12
          const lineHeight = fontSize * 1.2 // 行高
          const padding = 8 // 内边距
          const maxTextWidth = width - padding * 2 // 文本最大宽度

          // 处理文本换行
          let textLines = []
          let actualHeight = height
          const maxLines = cfg.maxLines || 4 // 最大行数，可通过配置传入
          if (cfg.label) {
            textLines = wrapText(cfg.label, maxTextWidth, fontSize, maxLines)
            // 根据文本行数动态调整节点高度
            const textHeight = textLines.length * lineHeight
            actualHeight = Math.max(height, textHeight + padding * 2)
          }

          // 绘制矩形节点
          const rect = group.addShape('rect', {
            attrs: {
              x: -width / 2,
              y: -actualHeight / 2,
              width: width,
              height: actualHeight,
              fill: style.fill || '#fff',
              stroke: style.stroke || '#666',
              lineWidth: style.lineWidth || 1,
              radius: style.radius || 4,
            },
            name: 'rect-shape',
          })

          // 绘制多行标签
          if (cfg.label && textLines.length > 0) {
            const startY = -(textLines.length - 1) * lineHeight / 2
            textLines.forEach((line, index) => {
              group.addShape('text', {
                attrs: {
                  text: line,
                  x: 0,
                  y: startY + index * lineHeight,
                  textAlign: 'center',
                  textBaseline: 'middle',
                  fill: '#000',
                  fontSize: fontSize,
                },
                name: 'text-shape',
              })
            })
          }

          // 如果有子节点，添加收缩按钮（在节点底部）
          // 检查是否有子节点
          const hasChildren = (cfg.children && Array.isArray(cfg.children) && cfg.children.length > 0) ||
            (cfg.data && cfg.data.children && Array.isArray(cfg.data.children) && cfg.data.children.length > 0)

          if (hasChildren) {
            // 获取 collapsed 状态
            let collapsed = false
            if (cfg.collapsed !== undefined) {
              collapsed = cfg.collapsed === true
            } else if (cfg.data && cfg.data.collapsed !== undefined) {
              collapsed = cfg.data.collapsed === true
            }

            // 获取子节点数量
            const childrenCount = (cfg.children && cfg.children.length) ||
              (cfg.data && cfg.data.children && cfg.data.children.length) || 0

            // 绘制按钮背景（小矩形）- 使用实际高度
            group.addShape('rect', {
              attrs: {
                x: -8,
                y: actualHeight / 2 - 4,
                width: 16,
                height: 16,
                fill: '#fff',
                stroke: '#A3B1BF',
                lineWidth: 1,
                radius: 2,
                cursor: 'pointer',
              },
              name: 'collapse-button-bg',
            })

            // 绘制按钮文本：收缩时显示数量，展开时显示 '-'
            const buttonText = collapsed ? String(childrenCount) : '-'
            group.addShape('text', {
              attrs: {
                x: 0,
                y: actualHeight / 2 + 4,
                text: buttonText,
                fill: '#666',
                fontSize: 12,
                fontWeight: 'bold',
                textAlign: 'center',
                textBaseline: 'middle',
                cursor: 'pointer',
              },
              name: 'collapse-button-text',
            })

            // 添加点击区域（更大的透明区域，便于点击）
            group.addShape('rect', {
              attrs: {
                x: -12,
                y: actualHeight / 2 - 8,
                width: 24,
                height: 24,
                fill: 'transparent',
                cursor: 'pointer',
              },
              name: 'collapse-button-area',
            })
          }

          return rect
        },
        // update 方法在 G6 4.x 中可能不会被自动调用，改用其他方式
        setState (name, value, item) {
          const group = item.getContainer()
          const rect = group.find(e => e.get('name') === 'rect-shape')
          if (name === 'selected') {
            if (value) {
              rect.attr('stroke', '#1890ff')
              rect.attr('lineWidth', 2)
            } else {
              const cfg = item.getModel()
              const style = cfg.style || {}
              rect.attr('stroke', style.stroke || '#666')
              rect.attr('lineWidth', style.lineWidth || 1)
            }
          }
        },
      })

      // 注册自定义正交折线边类型
      G6.registerEdge('orth-polyline', {
        draw (cfg, group) {
          const startPoint = cfg.startPoint || { x: 0, y: 0 }
          const endPoint = cfg.endPoint || { x: 0, y: 0 }

          // 计算中间点：从父节点垂直向下，在中间位置水平分开
          const midY = (startPoint.y + endPoint.y) / 2

          // 定义路径：垂直向下 -> 水平移动 -> 垂直向下
          const path = [
            ['M', startPoint.x, startPoint.y], // 起点（父节点底部）
            ['L', startPoint.x, midY],         // 垂直向下到中间位置
            ['L', endPoint.x, midY],          // 水平移动到子节点上方
            ['L', endPoint.x, endPoint.y],    // 垂直向下到终点（子节点顶部）
          ]

          // 绘制路径
          const shape = group.addShape('path', {
            attrs: {
              path: path,
              stroke: (cfg.style && cfg.style.stroke) || '#A3B1BF',
              lineWidth: (cfg.style && cfg.style.lineWidth) || 2,
            },
            name: 'orth-polyline-edge',
            draggable: false,
          })

          return shape
        },
        afterDraw (cfg, group) {
          // 确保路径正确更新
          const shape = group.find(e => e.get('name') === 'orth-polyline-edge')
          if (shape) {
            const startPoint = cfg.startPoint || { x: 0, y: 0 }
            const endPoint = cfg.endPoint || { x: 0, y: 0 }
            const midY = (startPoint.y + endPoint.y) / 2

            const path = [
              ['M', startPoint.x, startPoint.y],
              ['L', startPoint.x, midY],
              ['L', endPoint.x, midY],
              ['L', endPoint.x, endPoint.y],
            ]

            shape.attr('path', path)
          }
        },
        getPath (cfg) {
          const startPoint = cfg.startPoint || { x: 0, y: 0 }
          const endPoint = cfg.endPoint || { x: 0, y: 0 }
          const midY = (startPoint.y + endPoint.y) / 2

          return [
            ['M', startPoint.x, startPoint.y],
            ['L', startPoint.x, midY],
            ['L', endPoint.x, midY],
            ['L', endPoint.x, endPoint.y],
          ]
        },
      })

      // 保持您原有的自定义行为注册
      // 注意：TreeGraph 可能不支持 node:drag 事件，改用鼠标事件
      G6.registerBehavior('drag-node-with-drop', {
        getEvents () {
          return {
            'node:mousedown': 'onMouseDown',
            'mousemove': 'onMouseMove', // 画布上的鼠标移动事件
            'mouseup': 'onMouseUp', // 画布上的鼠标释放事件
          }
        },
        // 关键：添加 shouldBegin 方法，允许拖拽开始
        shouldBegin (e) {
          // 排除根节点
          try {
            const model = e.item.getModel()
            const rootData = graph.get('data')
            if (rootData && model.id === rootData.id) {
              return false
            }
          } catch (err) {
            // 忽略错误
          }
          return true
        },
        onMouseDown (e) {
          // 排除根节点
          try {
            const model = e.item.getModel()
            const rootData = graph.get('data')
            if (rootData && model.id === rootData.id) {
              return
            }
          } catch (err) {
            // 忽略错误
          }

          // 关键：检查是否点击了收缩按钮区域，如果是，不开始拖拽
          try {
            const group = e.item.getContainer()
            const clickArea = group.find(ele => ele.get('name') === 'collapse-button-area')
            if (clickArea) {
              const point = { x: e.x, y: e.y }
              const bbox = clickArea.getBBox()
              // 检查点击是否在按钮区域内
              if (
                point.x >= bbox.x &&
                point.x <= bbox.x + bbox.width &&
                point.y >= bbox.y &&
                point.y <= bbox.y + bbox.height
              ) {
                // 点击了收缩按钮，不开始拖拽，让点击事件正常处理
                return
              }
            }
          } catch (err) {
            // 忽略错误
          }

          // 记录初始位置，但不立即开始拖拽
          // 只有在鼠标移动一定距离后才开始拖拽（避免与点击冲突）
          this.origin = { x: e.x, y: e.y }
          this.dragging = false // 初始状态为 false
          this.target = e.item
          this.currentTarget = null
          this.mouseDownTime = Date.now() // 记录按下时间
          this.rafId = null // requestAnimationFrame ID
          this.lastUpdateTime = 0 // 上次更新时间
          this.pendingPoint = null // 待处理的鼠标位置
        },
        onMouseMove (e) {
          // 如果没有目标节点，不处理
          if (!this.target) return

          // 如果还没有开始拖拽，检查是否需要开始拖拽
          if (!this.dragging && this.origin) {
            // 计算鼠标移动距离
            const point = graph.getPointByClient(e.clientX, e.clientY)
            if (!point) return

            const dx = Math.abs(point.x - this.origin.x)
            const dy = Math.abs(point.y - this.origin.y)
            const distance = Math.sqrt(dx * dx + dy * dy)

            // 如果移动距离超过 5 像素，或者按下时间超过 200ms，开始拖拽
            const timeSinceDown = Date.now() - (this.mouseDownTime || 0)
            if (distance > 5 || timeSinceDown > 200) {
              this.dragging = true
              // 开始拖拽时更新节点样式
              try {
                const group = this.target.getContainer()
                const rect = group.find(ele => ele.get('name') === 'rect-shape')
                if (rect) {
                  rect.attr('opacity', 0.6)
                }
              } catch (err) {
                // 忽略错误
              }
            } else {
              // 移动距离太小，可能是点击，不开始拖拽
              return
            }
          }

          // 如果还没有开始拖拽，不处理
          if (!this.dragging) return

          // 获取画布坐标
          const point = graph.getPointByClient(e.clientX, e.clientY)
          if (!point) return

          // 保存待处理的鼠标位置
          this.pendingPoint = point

          // 使用 requestAnimationFrame 优化更新频率
          if (!this.rafId) {
            this.rafId = requestAnimationFrame(() => {
              this.updateDragPosition()
              this.rafId = null
            })
          }
        },
        updateDragPosition () {
          if (!this.dragging || !this.target || !this.pendingPoint) return

          const point = this.pendingPoint
          const now = Date.now()

          // 限制更新频率，每帧最多更新一次（约 60fps）
          if (now - this.lastUpdateTime < 16) {
            // 如果距离上次更新太近，延迟到下一帧
            if (!this.rafId) {
              this.rafId = requestAnimationFrame(() => {
                this.updateDragPosition()
                this.rafId = null
              })
            }
            return
          }

          this.lastUpdateTime = now

          // 更新节点位置
          try {
            const model = this.target.getModel()
            const oldX = model.x || 0
            const oldY = model.y || 0

            // 如果移动距离很小，跳过更新
            if (Math.abs(point.x - oldX) < 0.5 && Math.abs(point.y - oldY) < 0.5) {
              return
            }

            // 使用 updateItem 更新位置，但通过 requestAnimationFrame 已经节流了
            graph.updateItem(this.target, {
              x: point.x,
              y: point.y,
            })
          } catch (err) {
            // 忽略错误
          }

          // 优化节点查找：使用 G6 的 find 方法，只在需要时查找
          // 使用防抖机制，减少高亮更新的频率
          if (!this.highlightTimer) {
            this.highlightTimer = setTimeout(() => {
              this.updateHighlight(point)
              this.highlightTimer = null
            }, 50) // 50ms 防抖
          }
        },
        updateHighlight (point) {
          // 使用更高效的节点查找方式
          let hoverNode = null

          try {
            // 使用 G6 的 find 方法查找节点
            const nodes = graph.getNodes()
            const targetId = this.target.getModel().id

            // 优化：只检查可见节点，使用更简单的碰撞检测
            for (let i = 0; i < nodes.length; i++) {
              const node = nodes[i]
              if (node === this.target) continue

              try {
                const model = node.getModel()
                if (!model || model.id === targetId) continue

                // 使用简化的碰撞检测
                const nodeX = model.x || 0
                const nodeY = model.y || 0
                const nodeWidth = 80 // 固定节点宽度
                const nodeHeight = 40 // 固定节点高度

                // 检查点是否在节点范围内（考虑容差）
                if (
                  point.x >= nodeX - nodeWidth / 2 - 10 &&
                  point.x <= nodeX + nodeWidth / 2 + 10 &&
                  point.y >= nodeY - nodeHeight / 2 - 10 &&
                  point.y <= nodeY + nodeHeight / 2 + 10
                ) {
                  hoverNode = node
                  break // 找到第一个匹配的节点就停止
                }
              } catch (err) {
                // 忽略单个节点的错误
              }
            }
          } catch (err) {
            // 忽略查找错误
          }

          // 只在目标改变时更新高亮，减少不必要的样式更新
          if (hoverNode !== this.currentTarget) {
            // 清除之前的高亮
            if (this.currentTarget) {
              try {
                const group = this.currentTarget.getContainer()
                const rect = group && group.find(ele => ele.get('name') === 'rect-shape')
                if (rect) {
                  rect.attr('fill', '#fff')
                  rect.attr('stroke', '#666')
                }
              } catch (err) {
                // 忽略错误
              }
            }

            // 高亮新目标
            this.currentTarget = hoverNode
            if (hoverNode) {
              try {
                const group = hoverNode.getContainer()
                const rect = group && group.find(ele => ele.get('name') === 'rect-shape')
                if (rect) {
                  rect.attr('fill', '#f3f9ff')
                  rect.attr('stroke', '#1890ff')
                }
              } catch (err) {
                // 忽略错误
              }
            }
          }
        },
        onMouseUp (e) {
          // 清理 requestAnimationFrame
          if (this.rafId) {
            cancelAnimationFrame(this.rafId)
            this.rafId = null
          }

          // 清理高亮定时器
          if (this.highlightTimer) {
            clearTimeout(this.highlightTimer)
            this.highlightTimer = null
          }

          // 如果还没有开始拖拽（只是点击），清理状态，让点击事件正常处理
          if (!this.dragging) {
            this.target = null
            this.currentTarget = null
            this.origin = null
            this.mouseDownTime = null
            this.pendingPoint = null
            return
          }

          this.dragging = false

          // 保存引用，因为 forceRerender 会重新创建节点
          const targetNode = this.target
          const currentTargetNode = this.currentTarget

          // 先清除高亮（在 forceRerender 之前）
          if (currentTargetNode) {
            try {
              const group = currentTargetNode.getContainer()
              const rect = group && group.find(ele => ele.get('name') === 'rect-shape')
              if (rect) {
                rect.attr('fill', '#fff')
                rect.attr('stroke', '#666')
              }
            } catch (err) {
              // 忽略错误，节点可能已经被重新创建
            }
          }

          // 恢复拖拽节点的样式
          if (targetNode) {
            try {
              const group = targetNode.getContainer()
              const rect = group && group.find(ele => ele.get('name') === 'rect-shape')
              if (rect) {
                rect.attr('opacity', 1)
              }
            } catch (err) {
              // 忽略错误，节点可能已经被重新创建
            }
          }

          // 如果有目标节点，执行 drop 操作
          if (currentTargetNode && targetNode) {
            this.handleDrop(targetNode, currentTargetNode)
          } else {
            // 如果没有 drop，恢复节点位置（通过重新布局）
            this.forceRerender()
          }

          // 清理引用
          this.target = null
          this.currentTarget = null
          this.pendingPoint = null
          this.lastUpdateTime = 0
        },
        handleDrop (dragItem, dropItem) {
          try {
            const dropModel = dropItem.getModel()
            const dragModel = dragItem.getModel()

            if (!dropModel || !dragModel) return

            // 检查是否不能放置：
            // 1. 不能拖动到自己
            // 2. 不能拖动到自己的直接或间接子节点（防止循环引用）
            if (dropModel.id === dragModel.id || this.isDescendant(dragModel, dropModel)) {
              console.warn('不能拖动到自己的子节点，防止循环引用')
              return
            }

            // 先从原父节点移除（避免在添加时出现重复）
            const parent = dragItem.get('parent')
            let removedFromParent = false
            if (parent) {
              try {
                const parentModel = parent.getModel()
                if (parentModel && parentModel.children) {
                  // 创建新的 children 数组，移除被拖动的节点
                  const children = parentModel.children.filter(child => child && child.id !== dragModel.id)
                  // 更新父节点的 children
                  parentModel.children = children
                  graph.updateItem(parent, {
                    children: children,
                  })
                  removedFromParent = true
                }
              } catch (err) {
                console.warn('更新父节点失败:', err)
              }
            }

            // 添加到新父节点
            if (!dropModel.children) {
              dropModel.children = []
            }

            // 检查是否已存在（防止重复添加）
            const exists = dropModel.children.some(child => child && child.id === dragModel.id)
            if (!exists) {
              // 直接使用 dragModel 引用，保持数据结构完整性
              // 注意：dragModel 已经从原父节点移除，所以这里直接添加是安全的
              dropModel.children.push(dragModel)

              try {
                graph.updateItem(dropItem, {
                  children: [...dropModel.children],
                  collapsed: false,
                })
              } catch (err) {
                console.warn('更新目标节点失败:', err)
                // 如果更新失败，回滚：从新父节点移除，恢复到原父节点
                // 先从 dropModel.children 中移除
                const dropIndex = dropModel.children.findIndex(child => child && child.id === dragModel.id)
                if (dropIndex > -1) {
                  dropModel.children.splice(dropIndex, 1)
                }
                // 恢复到原父节点
                if (removedFromParent && parent) {
                  const parentModel = parent.getModel()
                  if (parentModel) {
                    parentModel.children = [...(parentModel.children || []), dragModel]
                    graph.updateItem(parent, {
                      children: parentModel.children,
                    })
                  }
                }
                // 不再继续执行 forceRerender
                return
              }
            } else {
              // 如果已存在，但已经从原父节点移除，需要恢复
              if (removedFromParent && parent) {
                const parentModel = parent.getModel()
                if (parentModel) {
                  parentModel.children = [...(parentModel.children || []), dragModel]
                  graph.updateItem(parent, {
                    children: parentModel.children,
                  })
                }
              }
            }

            // 延迟强制重新渲染，确保所有更新完成
            setTimeout(() => {
              this.forceRerender()
            }, 10)
          } catch (error) {
            console.error('handleDrop 错误:', error)
            // 即使出错也尝试重新布局
            try {
              this.forceRerender()
            } catch (e) {
              console.error('恢复布局失败:', e)
            }
          }
        },
        // 检查 child 是否是 parent 的后代节点（递归检查所有层级）
        isDescendant (parent, child) {
          if (!parent || !child || !parent.children) {
            return false
          }

          // 检查直接子节点
          if (parent.children.some(c => c && c.id === child.id)) {
            return true
          }

          // 递归检查所有子节点的后代
          for (let i = 0; i < parent.children.length; i++) {
            const subChild = parent.children[i]
            if (subChild && this.isDescendant(subChild, child)) {
              return true
            }
          }

          return false
        },
        // 保留原方法以兼容（已废弃，使用 isDescendant）
        isChild (parent, child) {
          return this.isDescendant(parent, child)
        },
        forceRerender () {
          const expandedNodes = []
          graph.getNodes().forEach(node => {
            const model = node.getModel()
            if (model.collapsed === false) {
              expandedNodes.push(model.id)
            }
          })

          const currentData = graph.save()
          graph.changeData(currentData)

          graph.getNodes().forEach(node => {
            const model = node.getModel()
            if (expandedNodes.includes(model.id)) {
              graph.updateItem(node, {
                collapsed: false,
              })
            }
          })

          graph.layout()
          graph.fitCenter()
        },
      })

      // 保持您原有的图形配置
      graph = new G6.TreeGraph({
        container: 'mind-container',
        width,
        height,
        linkCenter: true,
        modes: {
          default: [
            {
              type: 'collapse-expand',
              onChange: function (item, collapsed) {
                const data = item.getModel()
                data.collapsed = collapsed
                return true
              },
            },
            'drag-canvas',
            'zoom-canvas',
            'drag-node-with-drop', // 自定义行为，已添加 shouldBegin 和 shouldUpdate 确保事件正确触发
          ],
        },
        defaultNode: {
          type: 'tree-node-with-collapse',
          size: [80, 40],
          anchorPoints: [
            [0, 0.5],
            [1, 0.5],
          ],
          style: {
            fill: '#fff',
            stroke: '#666',
            lineWidth: 1,
            radius: 4,
          },
        },
        defaultEdge: {
          type: 'orth-polyline',
          style: {
            stroke: '#A3B1BF',
            lineWidth: 2,
          },
        },
        layout: {
          type: 'compactBox',
          direction: 'TB',
          getId: function (d) {
            return d.id
          },
          getHeight: function () {
            return 40
          },
          getWidth: function () {
            return 80
          },
          getVGap: function () {
            return 80
          },
          getHGap: function () {
            return 20
          },
        },
      })

      // 保持您原有的节点配置
      graph.node(function (node) {
        return {
          label: node.zoneName || node.id,
          labelCfg: {
            position: 'center',
            style: {
              textAlign: 'center',
              textBaseline: 'middle',
            },
          },
        }
      })

      // 初始化数据 - 严格使用您要求的数据结构
      const clonedData = JSON.parse(JSON.stringify(this.gData))
      // 设置2级及以下节点的收缩状态
      this.setCollapseStateByLevel(clonedData, 1)

      const treeData = {
        id: this.$store.getters.userInfo.tenant.shortName,
        label: this.$store.getters.userInfo.tenant.shortName,
        children: clonedData,
      }

      graph.data(treeData)
      graph.render()
      graph.fitCenter()



      // 辅助函数：更新所有节点的收缩按钮文本
      const updateAllCollapseButtons = () => {
        const nodes = graph.getNodes()
        nodes.forEach(node => {
          const model = node.getModel()
          if (model.children && model.children.length > 0) {
            const group = node.getContainer()
            const buttonText = group.find(ele => ele.get('name') === 'collapse-button-text')
            if (buttonText) {
              const isCollapsed = model.collapsed === true
              const childrenCount = model.children.length
              // 收缩时显示数量，展开时显示 '-'
              const text = isCollapsed ? String(childrenCount) : '-'
              buttonText.attr('text', text)
            }
          }
        })
      }

      // 监听布局完成事件，更新所有按钮文本
      // G6 4.x 可能不支持 afterlayout，改用其他方式
      const originalLayout = graph.layout.bind(graph)
      graph.layout = function () {
        const result = originalLayout()
        // 布局完成后更新按钮文本
        setTimeout(() => {
          updateAllCollapseButtons()
        }, 50)
        return result
      }

      // 添加节点点击事件，处理收缩按钮点击和拦截节点点击
      graph.on('node:click', (e) => {
        const node = e.item
        if (!node) {
          e.preventDefault && e.preventDefault()
          e.stopPropagation && e.stopPropagation()
          return
        }

        const nodeModel = node.getModel()
        const group = node.getContainer()

        // 检查是否点击了收缩按钮区域
        const clickArea = group.find(ele => ele.get('name') === 'collapse-button-area')

        if (clickArea) {
          try {
            // 获取点击位置
            const point = { x: e.x, y: e.y }
            const bbox = clickArea.getBBox()

            // 检查点击是否在按钮区域内
            if (
              point.x >= bbox.x &&
              point.x <= bbox.x + bbox.width &&
              point.y >= bbox.y &&
              point.y <= bbox.y + bbox.height
            ) {
              // 切换展开/收缩状态
              const newCollapsed = !nodeModel.collapsed

              // 更新节点状态
              graph.updateItem(node, {
                collapsed: newCollapsed,
              })

              // 立即更新按钮文本：收缩时显示数量，展开时显示 '-'
              const buttonText = group.find(ele => ele.get('name') === 'collapse-button-text')
              if (buttonText) {
                const childrenCount = nodeModel.children ? nodeModel.children.length : 0
                const text = newCollapsed ? String(childrenCount) : '-'
                buttonText.attr('text', text)
              }

              // 重新布局（布局完成后会自动更新所有按钮文本）
              graph.layout()
              graph.fitCenter()

              // 阻止事件传播
              e.preventDefault && e.preventDefault()
              e.stopPropagation && e.stopPropagation()
              return
            }
          } catch (error) {
            console.warn('处理按钮点击失败:', error)
          }
        }

        // 如果不是点击收缩按钮，拦截节点点击事件
        // 这里可以添加自定义的节点点击处理逻辑
        console.log('节点点击被拦截', nodeModel)
        e.preventDefault && e.preventDefault()
        e.stopPropagation && e.stopPropagation()
      })

      // 保存初始树结构（用于重置功能）
      if (this.gData && this.gData.length > 0) {
        this.initialTreeData = JSON.parse(JSON.stringify(this.gData))
      }

      // 保持您原有的数据监听
      this.$watch(
        'gData',
        (newVal) => {
          // 如果 initialTreeData 还没有保存，且新数据有值，则保存
          if (!this.initialTreeData && newVal && newVal.length > 0) {
            this.initialTreeData = JSON.parse(JSON.stringify(newVal))
          }

          // 克隆数据并设置2级及以下节点的收缩状态
          const clonedData = JSON.parse(JSON.stringify(newVal))
          this.setCollapseStateByLevel(clonedData, 1)

          graph.changeData({
            id: this.$store.getters.userInfo.tenant.shortName,
            label: this.$store.getters.userInfo.tenant.shortName,
            children: clonedData,
          })
          graph.refresh()
          graph.fitCenter()
        },
        { deep: true, immediate: false }
      )

      // 保持您原有的窗口大小处理
      window.addEventListener('resize', () => {
        if (!graph || graph.get('destroyed')) return
        graph.changeSize(container.scrollWidth, container.scrollHeight)
      })

      // 监听全屏状态变化
      document.addEventListener('fullscreenchange', this.handleFullscreenChange)
      document.addEventListener('webkitfullscreenchange', this.handleFullscreenChange)
      document.addEventListener('mozfullscreenchange', this.handleFullscreenChange)
      document.addEventListener('MSFullscreenChange', this.handleFullscreenChange)
    },

    // 搜索相关方法
    handleSearch () {
      if (!this.searchText || !this.searchText.trim()) {
        this.$message.warning('请输入搜索内容')
        return
      }

      // 清除之前的高亮
      this.clearHighlight()

      // 搜索匹配的节点
      this.searchResults = []
      this.findMatchingNodes(graph.save(), this.searchText.trim())

      if (this.searchResults.length === 0) {
        this.$message.info('未找到匹配的片区')
        return
      }

      // 显示第一个结果
      this.currentResultIndex = 0
      this.showSearchResult(this.searchResults[0])
    },
    onSearchChange (e) {
      // 清空搜索时清除高亮和结果
      if (!e.target.value) {
        this.clearHighlight()
        this.searchResults = []
        this.currentResultIndex = 0
      }
    },
    navigateSearchResult (direction) {
      if (this.searchResults.length === 0) return

      // 计算新索引
      let newIndex = this.currentResultIndex + direction
      if (newIndex < 0) newIndex = 0
      if (newIndex >= this.searchResults.length) newIndex = this.searchResults.length - 1

      this.currentResultIndex = newIndex
      this.showSearchResult(this.searchResults[newIndex])
    },
    findMatchingNodes (data, searchText, path = []) {
      if (!data) return

      const searchLower = searchText.toLowerCase()

      // 处理单个节点
      const processNode = (node, currentPath) => {
        const nodeName = node.zoneName || node.label || node.id || ''
        const nodeId = node.id

        // 如果匹配，保存节点信息和路径
        if (nodeName.toLowerCase().includes(searchLower)) {
          this.searchResults.push({
            id: nodeId,
            name: nodeName,
            path: [...currentPath, nodeId]
          })
        }

        // 递归搜索子节点
        if (node.children && Array.isArray(node.children)) {
          node.children.forEach(child => {
            processNode(child, [...currentPath, nodeId])
          })
        }
      }

      // 处理根节点或子节点数组
      if (Array.isArray(data)) {
        data.forEach(node => processNode(node, path))
      } else {
        processNode(data, path)
      }
    },
    showSearchResult (result) {
      if (!result || !graph) return

      try {
        // 1. 展开从根到目标节点的路径
        this.expandPath(result.path)

        // 2. 等待布局完成后定位和高亮节点
        setTimeout(() => {
          const targetNode = graph.findById(result.id)
          if (targetNode) {
            // 高亮节点
            this.highlightNode(targetNode)

            // 定位到节点（居中显示）
            graph.focusItem(targetNode, true, {
              easing: 'easeCubic',
              duration: 500,
            })
          }
        }, 300)
      } catch (error) {
        console.error('显示搜索结果失败:', error)
      }
    },
    expandPath (path) {
      if (!path || path.length === 0) return

      try {
        // 展开路径上的所有节点（除了最后一个目标节点）
        for (let i = 0; i < path.length - 1; i++) {
          const nodeId = path[i]
          const node = graph.findById(nodeId)
          if (node) {
            const model = node.getModel()
            if (model.collapsed === true) {
              graph.updateItem(node, {
                collapsed: false
              })
            }
          }
        }

        // 重新布局
        graph.layout()
      } catch (error) {
        console.error('展开路径失败:', error)
      }
    },
    highlightNode (node) {
      try {
        // 清除之前的高亮
        this.clearHighlight()

        // 高亮当前节点
        const group = node.getContainer()
        const rect = group.find(e => e.get('name') === 'rect-shape')
        if (rect) {
          rect.attr('fill', '#fffbe6')
          rect.attr('stroke', '#faad14')
          rect.attr('lineWidth', 3)
        }

        // 保存当前高亮的节点
        this.highlightedNode = node
      } catch (error) {
        console.error('高亮节点失败:', error)
      }
    },
    clearHighlight () {
      if (!this.highlightedNode) return

      try {
        const group = this.highlightedNode.getContainer()
        const rect = group.find(e => e.get('name') === 'rect-shape')
        if (rect) {
          rect.attr('fill', '#fff')
          rect.attr('stroke', '#666')
          rect.attr('lineWidth', 1)
        }
        this.highlightedNode = null
      } catch (error) {
        // 节点可能已被销毁，忽略错误
      }
    },
    handleFullscreen () {
      const wrapper = document.getElementById('mind-wrapper')
      if (!wrapper) return

      if (!this.isFullscreen) {
        // 进入全屏
        if (wrapper.requestFullscreen) {
          wrapper.requestFullscreen()
        } else if (wrapper.webkitRequestFullscreen) {
          wrapper.webkitRequestFullscreen()
        } else if (wrapper.mozRequestFullScreen) {
          wrapper.mozRequestFullScreen()
        } else if (wrapper.msRequestFullscreen) {
          wrapper.msRequestFullscreen()
        }
      } else {
        // 退出全屏
        if (document.exitFullscreen) {
          document.exitFullscreen()
        } else if (document.webkitExitFullscreen) {
          document.webkitExitFullscreen()
        } else if (document.mozCancelFullScreen) {
          document.mozCancelFullScreen()
        } else if (document.msExitFullscreen) {
          document.msExitFullscreen()
        }
      }
    },
    handleFullscreenChange () {
      const wrapper = document.getElementById('mind-wrapper')
      const container = document.getElementById('mind-container')
      if (!wrapper || !container) return

      const isFullscreen = !!(
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.mozFullScreenElement ||
        document.msFullscreenElement
      )

      this.isFullscreen = isFullscreen

      // 配置 message 容器（全屏时挂载到全屏容器内）
      const getMessageContainer = () => {
        if (isFullscreen) {
          return wrapper
        }
        return document.body
      }
      this.$message.config({
        getContainer: getMessageContainer
      })

      // 全屏状态改变后，更新图形大小
      if (graph && !graph.get('destroyed')) {
        setTimeout(() => {
          // 使用容器的实际高度（flex 布局会自动计算）
          const containerWidth = container.offsetWidth || container.scrollWidth
          const containerHeight = container.offsetHeight || container.scrollHeight
          graph.changeSize(containerWidth, containerHeight)
          graph.fitCenter()
        }, 100)
      }
    },
    handleReset () {
      if (!graph || !this.initialTreeData) {
        console.warn('重置失败：graph 或 initialTreeData 不存在', { graph: !!graph, initialTreeData: !!this.initialTreeData })
        return
      }

      // 清除搜索状态
      this.clearHighlight()
      this.searchText = ''
      this.searchResults = []
      this.currentResultIndex = 0

      // 保存当前展开状态
      const expandedNodes = []
      graph.getNodes().forEach(node => {
        const model = node.getModel()
        if (model.collapsed === false) {
          expandedNodes.push(model.id)
        }
      })

      // 构建重置数据
      const clonedData = JSON.parse(JSON.stringify(this.initialTreeData))
      // 设置2级及以下节点的收缩状态
      this.setCollapseStateByLevel(clonedData, 1)

      const treeData = {
        id: this.$store.getters.userInfo.tenant.shortName,
        label: this.$store.getters.userInfo.tenant.shortName,
        children: clonedData,
      }

      // 恢复到完整的初始树结构
      graph.changeData(treeData)

      // 重新布局
      graph.layout()

      // 恢复展开状态
      setTimeout(() => {
        graph.getNodes().forEach(node => {
          const model = node.getModel()
          if (expandedNodes.includes(model.id)) {
            graph.updateItem(node, {
              collapsed: false,
            })
          }
        })
        graph.fitCenter()
      }, 100)
    },
    handleSave () {
      // 获取全屏容器，如果全屏则挂载到全屏容器内，否则使用默认的 body
      let getContainer = () => document.body
      if (this.isFullscreen) {
        const wrapper = document.getElementById('mind-wrapper')
        if (wrapper) {
          getContainer = () => wrapper
        }
      }

      this.$confirm({
        title: '确认保存',
        content: '您确定要保存当前分区规划吗？',
        getContainer: getContainer, // 将弹窗挂载到全屏容器内
        onOk: () => {
          // 用户确认保存后的逻辑
          const currentData = graph.save()
          const zoneData = this.getZoneMetering(currentData.children)
          this.$emit('zone-data-update', zoneData)

          // 保存成功后刷新数据
          this.refreshDataAfterSave(currentData.children)
        },
        onCancel: () => {
          // 用户取消保存
          console.log('取消保存')
        },
      })
    },
    getZoneMetering (data) {
      const array = []
      console.log('原始数据:', data)

      // 使用箭头函数递归处理
      const processItems = (items, parentId = '0', level = 1) => {
        items.forEach(item => {
          array.push({
            id: item.id,
            pid: parentId,
            level: level,
          })

          if (item.children?.length > 0) {
            processItems(item.children, item.id, level + 1)
          }
        })
      }

      processItems(data)
      console.log('处理后的数组:', array)
      return array
    },
    refreshDataAfterSave (savedData) {
      if (!graph || graph.get('destroyed')) return

      try {
        // 更新初始树结构（保存当前状态作为新的初始状态，用于重置功能）
        if (savedData && savedData.length > 0) {
          this.initialTreeData = JSON.parse(JSON.stringify(savedData))
        }

        // 保存当前展开状态
        const expandedNodes = []
        graph.getNodes().forEach(node => {
          const model = node.getModel()
          if (model.collapsed === false) {
            expandedNodes.push(model.id)
          }
        })

        // 重新加载数据并刷新图形
        const clonedData = JSON.parse(JSON.stringify(savedData))
        // 设置2级及以下节点的收缩状态
        this.setCollapseStateByLevel(clonedData, 1)

        const treeData = {
          id: this.$store.getters.userInfo.tenant.shortName,
          label: this.$store.getters.userInfo.tenant.shortName,
          children: clonedData,
        }

        graph.changeData(treeData)
        graph.refresh()

        // 恢复展开状态
        setTimeout(() => {
          graph.getNodes().forEach(node => {
            const model = node.getModel()
            if (expandedNodes.includes(model.id)) {
              graph.updateItem(node, {
                collapsed: false,
              })
            }
          })
          graph.layout()
          graph.fitCenter()
        }, 50)

        // 显示保存成功提示（全屏时挂载到全屏容器内）
        this.showMessage('保存成功', 'success')
      } catch (error) {
        console.error('刷新数据失败:', error)
        this.showMessage('保存成功，但刷新数据时出现错误', 'error')
      }
    },
    showMessage (content, type = 'success') {
      // 配置 message 的容器（全屏时挂载到全屏容器内）
      const getContainer = () => {
        if (this.isFullscreen) {
          const wrapper = document.getElementById('mind-wrapper')
          if (wrapper) {
            return wrapper
          }
        }
        return document.body
      }

      // 使用 message.config 配置容器
      this.$message.config({
        getContainer: getContainer
      })

      // 根据类型显示不同的消息
      if (type === 'success') {
        this.$message.success(content)
      } else if (type === 'error') {
        this.$message.error(content)
      } else {
        this.$message.info(content)
      }
    },

  },
  beforeDestroy () {
    if (graph && !graph.get('destroyed')) {
      graph.destroy()
    }
    window.removeEventListener('resize', this.handleResize)
    // 移除全屏监听
    document.removeEventListener('fullscreenchange', this.handleFullscreenChange)
    document.removeEventListener('webkitfullscreenchange', this.handleFullscreenChange)
    document.removeEventListener('mozfullscreenchange', this.handleFullscreenChange)
    document.removeEventListener('MSFullscreenChange', this.handleFullscreenChange)
  },
}
</script>

<style lang="less" scoped>
#mind-wrapper {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
}

#mind-container {
  width: 100%;
  flex: 1;
  min-height: 0; // 确保 flex 子元素可以正确收缩
}

#mind-wrapper:fullscreen {
  background: #fff;
  padding: 16px;
  box-sizing: border-box;
}

#mind-wrapper:-webkit-full-screen {
  background: #fff;
  padding: 16px;
  box-sizing: border-box;
}

#mind-wrapper:-moz-full-screen {
  background: #fff;
  padding: 16px;
  box-sizing: border-box;
}

#mind-wrapper:-ms-fullscreen {
  background: #fff;
  padding: 16px;
  box-sizing: border-box;
}
</style>
